import { ArrowLeftOutlined, FileTextOutlined, LineChartOutlined } from '@ant-design/icons'
import { Badge, Button, Card, Col, DatePicker, Form, Input, Modal, Row, Space, Tabs } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'

const { TabPane } = Tabs

const ViewModal = ({ visible, handleCancel, viewItem, allfieldsData, fullListData, onEdit, onDelete, onSubmit, onApprove, onReject, listType, onReportChange = () => { } }) => {
    const [showReportForm, setShowReportForm] = React.useState(false)
    const [reportForm] = Form.useForm()
    const [reportList, setReportList] = React.useState([])
    const [editingReportId, setEditingReportId] = React.useState(null)

    React.useEffect(() => {
        const reports = Array.isArray(viewItem?.reports) ? viewItem.reports : []
        setReportList(reports)
        setShowReportForm(false)
        setEditingReportId(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewItem?.id])

    const getStatusColor = (statusId) => {
        const colors = {
            1: '#616161', // Draft
            2: '#01a9b4', // Submitted
            3: '#5cb85c', // Approved
            4: '#d9534f', // Rejected
        }
        return colors[statusId] || '#d9d9d9'
    }

    const getStatusText = (statusId) => {
        const texts = {
            1: 'Draft',
            2: 'Submitted',
            3: 'Approved',
            4: 'Rejected',
        }
        return texts[statusId] || 'Unknown'
    }

    const formatDate = (date) => {
        return moment(date).format('DD-MM-YYYY')
    }

    const formatTime = (time) => {
        return moment(time).format('HH:mm')
    }

    const footer = () => {
        const statusId = viewItem?.status?.id

        // Check if there's any previous item with type "Tour" in fullListData
        let hasPreviousTour = false
        if (Array.isArray(fullListData) && viewItem?.id) {
            const currentIndex = fullListData.findIndex(item => item?.id === viewItem.id)
            if (currentIndex > 0) {
                // Check all items before current item
                for (let i = 0; i < currentIndex; i++) {
                    if (fullListData[i]?.type?.name === 'Tour') {
                        hasPreviousTour = true
                        break
                    }
                }
            }
        }

        // Show buttons in a single row for statusId === 3 && listType === 'self'
        if (statusId === 3 && listType === 'self') {
            return (
                <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        type="default"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleCancel}
                    >
                        Go Back
                    </Button>
                    {!showReportForm && !hasPreviousTour && (
                        <Button type="primary" onClick={() => { reportForm.resetFields(); setEditingReportId(null); setShowReportForm(true) }}>Submitted Report</Button>
                    )}
                </div>
            )
        }
        if (statusId === 1) {
            // Draft: Edit, Delete, Submit
            return (
                <Space>
                    <Button onClick={() => onEdit && onEdit(viewItem)}>Edit</Button>
                    <Button danger onClick={() => onDelete && onDelete(viewItem)}>Delete</Button>
                    <Button type="primary" onClick={() => onSubmit && onSubmit(viewItem)}>Submit</Button>
                </Space>
            )
        }
        if (statusId === 2) {
            // Submitted: Approve/Reject (LM only, not self)
            const disabled = listType === 'self'
            return (
                <Space>
                    <Button type="primary" disabled={disabled} onClick={() => onApprove && onApprove(viewItem)}>Approve</Button>
                    <Button danger disabled={disabled} onClick={() => onReject && onReject(viewItem)}>Reject</Button>
                </Space>
            )
        }
        return null
    }

    if (!viewItem) return null

    const { type, from_time, to_time, to_date, from_date, purpose, tour_plans, status, description, tentative_cost, ...rest } = viewItem

    return (
        <Modal
            visible={visible}
            width={1000}
            title="Remote Work Details"
            footer={footer()}
            onCancel={handleCancel}
            destroyOnClose
        >
            <div style={{ padding: '20px 0' }}>
                <Tabs defaultActiveKey="overview" type="card">
                    <TabPane
                        tab={
                            <span>
                                <FileTextOutlined />
                                Overview
                            </span>
                        }
                        key="overview"
                    >
                        {/* Main Information Card */}
                        <Card style={{ marginBottom: '20px', position: 'relative', overflow: 'hidden' }}>
                            <div style={{
                                position: 'absolute',
                                top: '10px',
                                right: '-45px',
                                transform: 'rotate(38deg)',
                                backgroundColor: getStatusColor(status?.id),
                                color: 'white',
                                padding: '10px 50px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                borderRadius: '4px'
                            }}>
                                {getStatusText(status?.id)}
                            </div>

                            <Row gutter={[24, 16]}>
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Reference:</strong> {rest.id || 'N/A'}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Type:</strong> {type?.name || 'N/A'}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Purpose:</strong> {purpose?.name || 'N/A'}
                                    </div>
                                    {tentative_cost && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>Tentative Cost:</strong> {tentative_cost || 'N/A'}
                                        </div>
                                    )}
                                </Col>
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>From Date:</strong> {formatDate(from_date)}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>To Date:</strong> {formatDate(to_date)}
                                    </div>
                                    {description && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>Description:</strong> {description}
                                        </div>
                                    )}
                                    {/* {from_time && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>From Time:</strong> {formatTime(from_time)}
                                        </div>
                                    )}
                                    {to_time && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>To Time:</strong> {formatTime(to_time)}
                                        </div>
                                    )} */}
                                </Col>
                            </Row>
                        </Card>

                        {/* Trip Planning Details */}
                        {type?.id === 3 && tour_plans && tour_plans.length > 0 && (
                            <Card title="Tour Planning Details" style={{ marginBottom: '20px' }}>
                                <div style={{
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                }}>
                                    {/* Header */}
                                    <div style={{
                                        display: 'flex',
                                        backgroundColor: '#f5f5f5',
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #d9d9d9',
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                        color: '#666',
                                        textTransform: 'uppercase'
                                    }}>
                                        <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>DATE</div>
                                        <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>LOCATION</div>
                                        <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>PLANNED ACTIVITY</div>
                                        <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>MODE OF TRANSPORT</div>
                                        <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>DESCRIPTION</div>
                                    </div>

                                    {/* Rows */}
                                    <div>
                                        {tour_plans.map((plan, index) => (
                                            <div
                                                key={plan.id || index}
                                                style={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    padding: '16px 16px',
                                                    borderBottom: index < tour_plans.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                    alignItems: 'center',
                                                    backgroundColor: '#fff'
                                                }}
                                            >
                                                <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {formatDate(plan.date)}
                                                </div>
                                                <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {plan.location || 'N/A'}
                                                </div>
                                                <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {plan.planned_activities?.map(activity => activity.name).join(', ') || 'N/A'}
                                                </div>
                                                <div style={{ flex: '0 0 15%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {plan.transport?.name || 'N/A'}
                                                </div>
                                                <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {plan.note || 'N/A'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Submitted Report List */}
                        {reportList && reportList.length > 0 && (
                            <Card title="Submitted Report" style={{ marginBottom: '20px' }}>
                                <div style={{
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '6px',
                                    overflow: 'hidden'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        backgroundColor: '#f5f5f5',
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #d9d9d9',
                                        fontWeight: 'bold',
                                        fontSize: '12px',
                                        color: '#666',
                                        textTransform: 'uppercase'
                                    }}>
                                        <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>OUTCOMES</div>
                                        <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>FOLLOW UP ACTIONS</div>
                                        <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>DEADLINE</div>
                                    </div>
                                    <div>
                                        {reportList.map((rep, idx) => (
                                            <div
                                                key={rep.id || idx}
                                                style={{
                                                    display: 'flex',
                                                    gap: '12px',
                                                    padding: '16px 16px',
                                                    borderBottom: idx < reportList.length - 1 ? '1px solid #f0f0f0' : 'none',
                                                    alignItems: 'center',
                                                    backgroundColor: '#fff'
                                                }}
                                            >
                                                <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>{rep.outcomes || 'N/A'}</div>
                                                <div style={{ flex: '0 0 30%', paddingRight: '12px', paddingLeft: '8px' }}>{rep.follow_up_actions || 'N/A'}</div>
                                                <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>{rep.deadline ? moment(rep.deadline).format('DD-MM-YYYY') : 'N/A'}</div>
                                                <div style={{ flex: '0 0 20%', paddingRight: '12px', paddingLeft: '8px' }}>
                                                    {(listType === 'self' && rep?.status?.id === 1) && (
                                                        <div style={{ marginLeft: 8 }}>
                                                            <Space>
                                                                <Button size="small" onClick={() => {
                                                                    reportForm.setFieldsValue({
                                                                        outcomes: rep.outcomes,
                                                                        follow_up_actions: rep.follow_up_actions,
                                                                        deadline: rep.deadline ? moment(rep.deadline) : undefined,
                                                                    })
                                                                    setEditingReportId(rep.id)
                                                                    setShowReportForm(true)
                                                                }}>Edit</Button>
                                                                <Button size="small" danger onClick={async () => {
                                                                    try {
                                                                        const { RemoteWorkReportService } = await import('services')
                                                                        const res = await RemoteWorkReportService.remove({ report_id: rep.id })
                                                                        if (res?.success) {
                                                                            setReportList((prev) => prev.filter(r => r.id !== rep.id))
                                                                            onReportChange('delete', rep.id)
                                                                        }
                                                                    } catch (e) { console.error(e) }
                                                                }}>Delete</Button>
                                                            </Space>
                                                            <div style={{ marginTop: 8 }}>
                                                                <Button size="small" type="primary" onClick={async () => {
                                                                    try {
                                                                        const { RemoteWorkReportService } = await import('services')
                                                                        const res = await RemoteWorkReportService.patchAction('/submit', { report_id: rep.id })
                                                                        if (res) {
                                                                            setReportList((prev) => prev.map(r => r.id === rep.id ? res : r))
                                                                            onReportChange('submit', res)
                                                                        }
                                                                    } catch (e) { console.error(e) }
                                                                }}>Submit</Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        )}
                    </TabPane>

                    <TabPane
                        tab={
                            <span>
                                <LineChartOutlined />
                                Details
                            </span>
                        }
                        key="details"
                    >
                        <Card title="Additional Details">
                            <Row gutter={[24, 16]}>
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Created At:</strong> {moment(rest.created_at).format('DD-MM-YYYY HH:mm')}
                                    </div>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Updated At:</strong> {moment(rest.updated_at).format('DD-MM-YYYY HH:mm')}
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div style={{ marginBottom: '12px' }}>
                                        <strong>Status:</strong>
                                        <Badge
                                            color={getStatusColor(status?.id)}
                                            text={getStatusText(status?.id)}
                                            style={{ marginLeft: '8px' }}
                                        />
                                    </div>
                                    {rest.approved_by && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <strong>Approved By:</strong> {rest.approved_by}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    </TabPane>
                </Tabs>

                {/* Submitted Report Form */}
                {showReportForm && (
                    <Card title="Submitted Report" style={{ marginTop: 16 }}>
                        <Form
                            form={reportForm}
                            layout="vertical"
                            onFinish={async (values) => {
                                // POST to remote_work_report
                                try {
                                    const { RemoteWorkReportService } = await import('services')
                                    const payload = {
                                        remote_work_id: rest.id,
                                        outcomes: values.outcomes,
                                        follow_up_actions: values.follow_up_actions,
                                        deadline: values.deadline?.format('YYYY-MM-DD'),
                                    }
                                    if (!editingReportId) {
                                        const res = await RemoteWorkReportService.create(payload)
                                        if (res?.id || res?.success) {
                                            const created = res?.id ? res : { ...payload, id: res.id, status: { id: 1, name: 'Draft' } }
                                            setReportList((prev) => [...prev, created]) // Append instead of replace
                                            setShowReportForm(false)
                                            reportForm.resetFields()
                                        }
                                    } else {
                                        const payloadWithId = { id: editingReportId, ...payload }
                                        const res = await RemoteWorkReportService.patchAction('', payloadWithId)
                                        if (res?.id || res?.success) {
                                            const updated = res?.id ? res : { ...payloadWithId, status: { id: 1, name: 'Draft' } }
                                            setReportList((prev) => prev.map(r => r.id === editingReportId ? updated : r))
                                            setShowReportForm(false)
                                            setEditingReportId(null)
                                            reportForm.resetFields()
                                        }
                                    }
                                } catch (e) {
                                    // eslint-disable-next-line no-console
                                    console.error(e)
                                }
                            }}
                        >
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="outcomes"
                                        label="Outcomes"
                                        rules={[{ required: true, message: 'Please enter outcomes' }]}
                                    >
                                        <Input.TextArea rows={3} placeholder="Outcomes" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="follow_up_actions"
                                        label="Follow up actions"
                                        rules={[{ required: true, message: 'Please enter follow up actions' }]}
                                    >
                                        <Input.TextArea rows={3} placeholder="Follow up actions" />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item
                                        name="deadline"
                                        label="Deadline"
                                        rules={[{ required: true, message: 'Please select deadline' }]}
                                    >
                                        <DatePicker className="gx-w-100" format="DD-MM-YYYY" />
                                    </Form.Item>
                                </Col>
                                <Col span={16} style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                                    <Space>
                                        <Button onClick={() => { reportForm.resetFields(); setEditingReportId(null); setShowReportForm(false) }}>Cancel</Button>
                                        <Button type="primary" onClick={() => reportForm.submit()}>Save</Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                )}
            </div>
        </Modal>
    )
}

ViewModal.propTypes = {
    visible: PropTypes.bool,
    handleCancel: PropTypes.func.isRequired,
    viewItem: PropTypes.object,
    allfieldsData: PropTypes.object,
    fullListData: PropTypes.array,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    onSubmit: PropTypes.func,
    onApprove: PropTypes.func,
    onReject: PropTypes.func,
    listType: PropTypes.string,
    onReportChange: PropTypes.func,
}

ViewModal.defaultProps = {
    visible: false,
    viewItem: null,
    allfieldsData: {},
    fullListData: [],
    onEdit: undefined,
    onDelete: undefined,
    onSubmit: undefined,
    onApprove: undefined,
    onReject: undefined,
    listType: 'self',
    onReportChange: () => { },
}

export default ViewModal
